import React from 'react'
import moment from 'moment'
// @src imports
import { Button, Icon } from 'src/chrome'
import { IconTypeOrIconString } from 'src/app/types'
import { Maybe, MinimalReminderFragment } from 'src/graphql'

import styles from './reminder.module.scss'
import { Text } from '@sendoutcards/quantum-design-ui'

const gradients = {
  pink: 'linear-gradient(115deg, #ff5689, #e687ff)',
  blue: 'linear-gradient(115deg, #6db0ff, #7de8f6)',
  orange: 'linear-gradient(28deg, #DD2476 0%, #FE5030 100%)',
}

const greetings = (name: string, type: string) => {
  const list = [
    `Wish ${name} a happy ${type}`,
    `Celebrate with ${name}`,
    `${name}'s ${type} is coming up!`,
  ]

  const greeting = list[Math.floor(Math.random() * list.length)]
  return greeting
}

const properties = (
  reminder: MinimalReminderFragment,
): {
  color: keyof typeof gradients
  icon: IconTypeOrIconString
  iconColor: string
  viewBox: string
} => {
  if (reminder.newType === 'ANNIVERSARY') {
    return {
      color: 'pink',
      icon: 'CHEERS',
      iconColor: '#F758A1',
      viewBox: '0 0 33 32',
    }
  } else if (reminder.newType === 'BIRTHDAY') {
    return {
      color: 'blue',
      icon: 'CONFETTI',
      iconColor: '#6db0ff',
      viewBox: '0 0 31 32',
    }
  } else {
    return {
      color: 'orange',
      icon: 'CAMPAIGNS',
      iconColor: '#DD2476',
      viewBox: '0 0 25 32',
    }
  }
}

interface ReminderProps {
  onDismiss: (reminder: MinimalReminderFragment) => void
  onSendCard: (contact: {
    id: string
    firstName: string
    lastName: string
    birthday: Maybe<{
      day: number
      month: number
    }>
    anniversary: Maybe<{
      day: number
      month: number
    }>
  }) => void
  openCampaign: (campaignId: string) => void
  reminder: MinimalReminderFragment
}

const Reminder: React.FC<ReminderProps> = props => {
  const { onDismiss, onSendCard, openCampaign, reminder } = props
  const { campaign } = reminder
  const { color, icon, viewBox, iconColor } = properties(reminder)
  return (
    <div className={styles.reminder}>
      <div className={styles.leftContainer}>
        <Icon icon={icon} viewBox={viewBox} color={iconColor} size={35} />
        <div
          className={styles.date}
          style={{ backgroundImage: gradients[color] }}
        >
          <Text type="footnote" color="accent" alignment="center">
            {reminder.eventDate
              ? moment(reminder.eventDate).format('MMM Do')
              : reminder.newType === 'CAMPAIGN'
              ? 'Campaign'
              : null}
          </Text>
        </div>
      </div>
      {campaign === null || campaign === undefined ? (
        <div className={styles.body}>
          <div className={styles.details}>
            <Text
              type="largeBody"
              color="primaryHeading"
            >{`${reminder.contact.firstName} ${reminder.contact.lastName}`}</Text>
            <Text type="caption" color="primaryHeading">
              {reminder.title}
            </Text>
          </div>
          <div className={styles.description}>
            <Text type="body">
              {greetings(reminder.contact.firstName, reminder.title)}
            </Text>
            <Text type="caption">{reminder.message ?? ''}</Text>
          </div>
          <Button
            title={'Send a Card'}
            buttonColor={'pink'}
            className={styles.sendButton}
            onClick={() => onSendCard(reminder.contact)}
          />
        </div>
      ) : (
        <div className={styles.body}>
          <div className={styles.details} style={{ flexGrow: 1 }}>
            <Text type="largeBody" color="primaryHeading">
              {campaign.name}
            </Text>
          </div>
          <Button
            title={'View Campaign'}
            onClick={() => openCampaign(campaign.id)}
          />
        </div>
      )}
      <span
        className={styles.dismissButton}
        onClick={() => onDismiss(reminder)}
      >
        <Icon icon={'CLOSE'} color={'#6f8394'} size={18} />
      </span>
    </div>
  )
}

export default Reminder
